import React, { useEffect, useState } from 'react';
import { X, Sparkles, Check, Loader2, AlertCircle, ArrowRight, Search, Mail, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';

/**
 * Modal de enriquecimento de contato via Apollo com CASCATA de estrategias.
 *
 * Frente B (resumo_enrich_employments 2026-04-26):
 *   1. Se contato.apollo_person_id existe → agent-people-enrich direto (1 credito).
 *   2. Senao → Search Apollo por nome + empresa atual (1 credito search).
 *      - 1 candidato → enrich direto (1 credito). total 2.
 *      - N candidatos → humano escolhe.
 *      - 0 candidatos → estrategia 3.
 *   3. Search Apollo sem filtro de empresa — nome amplo
 *      (permite detectar que pessoa saiu para outra empresa; humano decide).
 *   4. Apollo Enrich por linkedin_url direto (se contato ja tem person_linkedin_url).
 *      Util quando a pessoa trocou de nome/empresa mas o LinkedIn ainda e valido.
 *      Consome 1 credito extra. v1.1.0 da edge function suporta este input.
 *
 * Pre-processamento: extrai sobrenome do email se contato nao tem last_name.
 *
 * Props:
 *   contato, empresa, userEmail, onClose(), onApplied(updates)
 */
const CAMPOS_DIFF = [
  { key: 'title',              label: 'Cargo',         apolloKey: 'title' },
  { key: 'email',              label: 'Email',         apolloKey: 'email' },
  { key: 'phone',              label: 'Telefone',      apolloKey: 'phone' },
  { key: 'mobile',             label: 'Celular',       apolloKey: 'mobile_phone' },
  { key: 'apollo_personal_email', label: 'Email pessoal', apolloKey: 'personal_emails',
    transform: (v) => (Array.isArray(v) ? v[0] || null : v) },
  { key: 'person_linkedin_url',label: 'LinkedIn',      apolloKey: 'person_linkedin_url' },
  { key: 'senioridade',        label: 'Senioridade',   apolloKey: 'seniority' },
  { key: 'departamento',       label: 'Departamento',  apolloKey: 'department' },
  { key: 'first_name',         label: 'Primeiro nome', apolloKey: 'first_name' },
  { key: 'last_name',          label: 'Sobrenome',     apolloKey: 'last_name' },
];

// Reads Apollo value with optional transform (e.g. unwrap personal_emails array).
const getApolloVal = (apolloData, f) => {
  const raw = apolloData?.[f.apolloKey];
  return f.transform ? f.transform(raw) : raw;
};

/**
 * Extrai sobrenome provavel do local-part do email.
 *  "arthur.ceolin@conx.com.br" → "Ceolin"
 *  "pamela.oliveira@lavvi.com" → "Oliveira"
 *  "eduardo@aston.com"         → null
 * Ignora sufixos < 3 caracteres ou com numeros.
 */
export const extractLastNameFromEmail = (email) => {
  if (!email || !email.includes('@')) return null;
  const local = email.split('@')[0];
  const parts = local.split('.');
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1];
  if (last.length < 3 || /\d/.test(last)) return null;
  return last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();
};

const ApolloEnrichContactModal = ({ contato, empresa, userEmail, onClose, onApplied }) => {
  // step: 'loading' | 'candidates' | 'diff' | 'applying' | 'done'
  const [step, setStep] = useState('loading');
  // strategy: 'init' | 'enrich_direct' | 'search_current' | 'search_broad' | 'enrich_linkedin' | 'found' | 'not_found'
  const [strategy, setStrategy] = useState('init');
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [apolloData, setApolloData] = useState(null);
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [strategyLog, setStrategyLog] = useState([]);

  // Frente C: toggles de churn (mudanca de empresa)
  const [applyEndEmployment, setApplyEndEmployment] = useState(true);
  const [applyCreateEmployment, setApplyCreateEmployment] = useState(true);

  // Manual LinkedIn URL retry (when cascade fails)
  const [manualLinkedinUrl, setManualLinkedinUrl] = useState('');
  const [retrying, setRetrying] = useState(false);

  // Enrichment options (mirror Apollo.io toggles; cost increases per flag)
  const [enrichEmails, setEnrichEmails] = useState(true);
  const [enrichPhone, setEnrichPhone] = useState(false);

  const inferredLastName = !contato?.last_name ? extractLastNameFromEmail(contato?.email) : null;
  const effectiveLastName = contato?.last_name || inferredLastName || '';
  const nomeCompleto = `${contato?.first_name || ''} ${effectiveLastName}`.trim();
  const empresaLabel = empresa?.company_name || empresa?.empresa || '';

  const logStrategy = (label, status) => {
    setStrategyLog(prev => [...prev, { label, status }]);
  };

  useEffect(() => {
    if (!contato?.id) return;
    let cancelled = false;
    (async () => {
      try {
        await runCascade(cancelled);
      } catch (err) {
        if (!cancelled) { setError(err.message || String(err)); setStep('done'); }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contato?.id]);

  const runCascade = async (cancelled) => {
    // Estrategia 1: enrich direto por apollo_person_id
    if (contato.apollo_person_id) {
      setStrategy('enrich_direct');
      logStrategy('Apollo Enrich direto (apollo_person_id)', 'running');
      const enr = await enrichByField({ apollo_person_ids: [contato.apollo_person_id] });
      if (cancelled) return;
      if (enr) {
        logStrategy('Apollo Enrich direto (apollo_person_id)', 'ok');
        finalizeDiff(enr);
        return;
      }
      logStrategy('Apollo Enrich direto (apollo_person_id)', 'fail');
    }

    // Estrategia 2: search nome + empresa
    if (nomeCompleto && empresaLabel) {
      setStrategy('search_current');
      logStrategy(`Busca Apollo: "${nomeCompleto}" @ ${empresaLabel}`, 'running');
      const found = await searchApollo({ q_person_name: nomeCompleto, q_organization_name: empresaLabel, per_page: 5 });
      if (cancelled) return;
      if (found.length === 1) {
        logStrategy(`Busca Apollo: "${nomeCompleto}" @ ${empresaLabel}`, 'ok · 1 match');
        const enr = await enrichByField({ apollo_person_ids: [found[0].apollo_person_id] });
        if (enr) { finalizeDiff(enr); return; }
      } else if (found.length > 1) {
        logStrategy(`Busca Apollo: "${nomeCompleto}" @ ${empresaLabel}`, `ok · ${found.length} matches`);
        setCandidates(found);
        setStep('candidates');
        setStrategy('found');
        return;
      } else {
        logStrategy(`Busca Apollo: "${nomeCompleto}" @ ${empresaLabel}`, '0 resultados');
      }
    }

    // Estrategia 3: search amplo (sem filtro de empresa) — pessoa pode ter saido
    if (nomeCompleto) {
      setStrategy('search_broad');
      logStrategy(`Busca ampla: "${nomeCompleto}" (sem filtro de empresa)`, 'running');
      const found = await searchApollo({ q_person_name: nomeCompleto, per_page: 5 });
      if (cancelled) return;
      if (found.length === 1) {
        logStrategy(`Busca ampla: "${nomeCompleto}"`, 'ok · 1 match');
        const enr = await enrichByField({ apollo_person_ids: [found[0].apollo_person_id] });
        if (enr) { finalizeDiff(enr); return; }
      } else if (found.length > 1) {
        logStrategy(`Busca ampla: "${nomeCompleto}"`, `ok · ${found.length} matches`);
        setCandidates(found);
        setStep('candidates');
        setStrategy('found');
        return;
      } else {
        logStrategy(`Busca ampla: "${nomeCompleto}"`, '0 resultados');
      }
    }

    // Estrategia 4: enrich por linkedin_url (se ja salvo no contato)
    if (contato.person_linkedin_url) {
      setStrategy('enrich_linkedin');
      logStrategy(`Apollo Enrich by linkedin_url`, 'running');
      const enr = await enrichByField({ linkedin_urls: [contato.person_linkedin_url] });
      if (cancelled) return;
      if (enr) { logStrategy(`Apollo Enrich by linkedin_url`, 'ok'); finalizeDiff(enr); return; }
      logStrategy(`Apollo Enrich by linkedin_url`, 'fail');
    }

    // Sem sorte em nenhuma estrategia
    setStrategy('not_found');
    setStep('done');
    setError('Nenhuma das estrategias encontrou o contato no Apollo. Tente revisar nome/empresa ou preencher LinkedIn URL.');
  };

  const enrichByField = async (payload) => {
    const { data, error: eErr } = await supabase.functions.invoke('agent-people-enrich', {
      body: {
        ...payload,
        create_in_crm: false,
        decided_by: userEmail || 'ui_enrich_contact',
        reveal_personal_emails: enrichEmails,
        reveal_phone_number: enrichPhone,
      },
    });
    if (eErr) throw eErr;
    return (data?.data?.enriched || [])[0] || null;
  };

  const handleManualLinkedinRetry = async () => {
    const url = manualLinkedinUrl.trim();
    if (!url || !url.includes('linkedin.com/in/')) return;

    setRetrying(true);
    setError(null);
    logStrategy('Apollo Enrich by linkedin_url (manual)', 'running');

    try {
      const enr = await enrichByField({ linkedin_urls: [url] });
      if (enr) {
        logStrategy('Apollo Enrich by linkedin_url (manual)', 'ok');
        // Also save the URL to the contact immediately
        await supabase.from('contatos').update({
          person_linkedin_url: url,
          updated_by: userEmail || 'ui_enrich_manual_linkedin',
        }).eq('id', contato.id);
        finalizeDiff(enr);
      } else {
        logStrategy('Apollo Enrich by linkedin_url (manual)', 'fail');
        setError('LinkedIn URL não retornou dados do Apollo. Verifique se a URL está correta.');
      }
    } catch (err) {
      logStrategy('Apollo Enrich by linkedin_url (manual)', 'fail');
      setError(`Erro: ${err.message || String(err)}`);
    } finally {
      setRetrying(false);
    }
  };

  const searchApollo = async (filters) => {
    const { data, error: sErr } = await supabase.functions.invoke('agent-people-search', {
      body: {
        filters,
        decided_by: userEmail || 'ui_enrich_contact',
        auto_classify: false,
        auto_enrich_top_n: 0,
      },
    });
    if (sErr) throw sErr;
    return data?.data?.candidates || data?.candidates || data?.results || [];
  };

  const finalizeDiff = (enr) => {
    setApolloData(enr);
    const sel = new Set();
    for (const f of CAMPOS_DIFF) {
      const cv = contato[f.key];
      const av = getApolloVal(enr, f);
      if (av && String(av).trim() && String(av).trim() !== String(cv || '').trim()) {
        sel.add(f.key);
      }
    }
    setSelectedFields(sel);
    setStep('diff');
  };

  const handleEscolherCandidato = async (cand) => {
    setStep('loading');
    try {
      const enr = await enrichByField({ apollo_person_ids: [cand.apollo_person_id] });
      if (!enr) throw new Error('Apollo nao retornou dados do candidato.');
      finalizeDiff(enr);
    } catch (err) {
      setError(err.message);
      setStep('done');
    }
  };

  const handleToggleField = (key) => {
    setSelectedFields(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const handleApplyFields = async () => {
    if (!apolloData || selectedFields.size === 0) return;
    setStep('applying');
    setError(null);

    const updates = {};
    const evidenceRows = [];
    for (const f of CAMPOS_DIFF) {
      if (!selectedFields.has(f.key)) continue;
      const newV = getApolloVal(apolloData, f);
      if (!newV || String(newV).trim() === '') continue;
      updates[f.key] = newV;
      evidenceRows.push({
        entity_type: 'contact',
        entity_id: contato.id,
        agent: 'apollo_enrich',
        field_name: f.key,
        suggested_value: newV,
        previous_value: contato[f.key] || null,
        human_decision: 'approved',
        decided_by: userEmail || 'desconhecido',
        confidence: 0.9,
        reasoning: 'Aplicacao manual de enriquecimento Apollo (preview -> approve)',
        metadata: {
          source: 'ui_apollo_enrich_preview',
          strategy,
          apollo_person_id: apolloData.apollo_person_id,
          inferred_last_name_from_email: inferredLastName || null,
          old_value: contato[f.key] || null,
        },
      });
    }

    if (!contato.apollo_person_id && apolloData.apollo_person_id) {
      updates.apollo_person_id = apolloData.apollo_person_id;
    }

    // Frente C: detectar churn
    const apolloCompanyName = apolloData.company_name
      && apolloData.company_name.trim();
    const crmCompanyName = empresaLabel && empresaLabel.trim();
    const churnDetected = apolloCompanyName && crmCompanyName
      && apolloCompanyName.toLowerCase() !== crmCompanyName.toLowerCase();

    try {
      const hoje = new Date().toISOString().slice(0, 10);

      // 1. UPDATE campos do contato
      if (Object.keys(updates).length > 0) {
        const { error: upErr } = await supabase.from('contatos').update(updates).eq('id', contato.id);
        if (upErr) throw upErr;
      }

      // 2. Churn: encerrar emprego atual
      if (churnDetected && applyEndEmployment && empresa?.id) {
        const { error: endErr } = await supabase
          .from('contact_employments')
          .update({
            is_current: false,
            end_date: hoje,
            last_verified_at: new Date().toISOString(),
            updated_by: `ui_apollo_enrich:${userEmail || 'desconhecido'}`,
          })
          .eq('contact_id', contato.id)
          .eq('company_id', empresa.id)
          .eq('is_current', true);
        if (endErr) console.warn('[enrich churn] end_employment nao-fatal:', endErr);
      }

      // 3. Churn: criar novo emprego na empresa nova (find_or_create empresa)
      if (churnDetected && applyCreateEmployment && apolloCompanyName) {
        // Find empresa no CRM (case-insensitive via ilike OR match case exato MAIUSCULO)
        const empresaUpper = apolloCompanyName.toUpperCase().replace(/&/g, ' E ').replace(/\s+/g, ' ').trim();
        let novaEmpresaId = null;
        try {
          const { data: existing } = await supabase
            .from('empresas')
            .select('id')
            .ilike('empresa', empresaUpper)
            .limit(1);
          if (existing && existing[0]) {
            novaEmpresaId = existing[0].id;
          } else {
            const { data: created, error: cErr } = await supabase
              .from('empresas')
              .insert({
                empresa: empresaUpper,
                company_name: apolloCompanyName,
                apollo_id: apolloData.company_apollo_id || null,
                papel_negocio: 'buyer',
                status_operacional: 'active',
              })
              .select('id')
              .single();
            if (cErr) {
              console.warn('[enrich churn] create_empresa falhou:', cErr);
            } else {
              novaEmpresaId = created.id;
            }
          }
        } catch (e) {
          console.warn('[enrich churn] find_or_create falhou:', e);
        }

        if (novaEmpresaId) {
          const { error: insErr } = await supabase
            .from('contact_employments')
            .insert({
              contact_id: contato.id,
              company_id: novaEmpresaId,
              is_current: true,
              start_date: hoje,
              job_title: apolloData.title || null,
              seniority: apolloData.seniority || null,
              department: apolloData.department || null,
              employment_email: apolloData.email || null,
              employment_phone: apolloData.phone || null,
              verification_source: 'apollo',
              verification_confidence: apolloData.email_verified ? 1.0 : 0.6,
              last_verified_at: new Date().toISOString(),
              apollo_employment_id: apolloData.apollo_person_id,
              created_by: `ui_apollo_enrich:${userEmail || 'desconhecido'}`,
              updated_by: `ui_apollo_enrich:${userEmail || 'desconhecido'}`,
              metadata: { source: 'ui_apollo_enrich_preview', strategy },
            });
          if (insErr) console.warn('[enrich churn] create_employment nao-fatal:', insErr);
        }

        // Evidence do churn
        evidenceRows.push({
          entity_type: 'contact',
          entity_id: contato.id,
          agent: 'apollo_enrich',
          field_name: 'company_change',
          suggested_value: { de: crmCompanyName, para: apolloCompanyName },
          previous_value: crmCompanyName,
          human_decision: 'approved',
          decided_by: userEmail || 'desconhecido',
          confidence: 0.9,
          reasoning: `Apollo indica que ${contato.first_name || '—'} saiu de ${crmCompanyName} para ${apolloCompanyName}`,
          metadata: {
            source: 'ui_apollo_enrich_preview',
            strategy,
            ended_employment: applyEndEmployment,
            created_new_employment: applyCreateEmployment,
            nova_empresa_id: novaEmpresaId,
            apollo_company_id: apolloData.company_apollo_id || null,
          },
        });
      }

      // 4. Evidence trail por campo + churn (se houver)
      if (evidenceRows.length > 0) {
        await supabase.from('ai_evidence').insert(evidenceRows).then(() => null).catch(e => {
          console.warn('[enrich] evidence insert nao-fatal:', e);
        });
      }

      if (onApplied) onApplied({ updates, apolloData, strategy, churnDetected });
      setStep('done');
    } catch (err) {
      setError(err.message);
      setStep('diff');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10010] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-amber-500" />
            <h3 className="text-sm font-semibold text-slate-800">
              Enriquecer contato via Apollo
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-4">
          <div className="text-xs text-slate-600 mb-3">
            <span className="font-medium">{nomeCompleto || '—'}</span>
            {' · '}
            {contato.title || '—'}
            {' @ '}
            {empresaLabel || '—'}
          </div>

          {step !== 'diff' && step !== 'done' && step !== 'applying' && (
            <div className="bg-slate-50 border border-slate-200 rounded p-2 mb-3 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Opções de enriquecimento</div>
              <div className="flex gap-4 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={enrichEmails} onChange={(e) => setEnrichEmails(e.target.checked)} className="rounded" />
                  <span>📧 Emails pessoais</span>
                  <span className="text-slate-400">(1-4 créditos)</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={enrichPhone} onChange={(e) => setEnrichPhone(e.target.checked)} className="rounded" />
                  <span>📱 Telefone</span>
                  <span className="text-slate-400">(8-12 créditos)</span>
                </label>
              </div>
              {enrichPhone && (
                <div className="text-[10px] text-amber-600 flex items-center gap-1">
                  ⚠️ Telefone é entregue de forma assíncrona (pode levar alguns minutos após aplicar)
                </div>
              )}
            </div>
          )}

          {inferredLastName && (
            <div className="mb-3 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <Mail size={11} className="flex-shrink-0 mt-0.5" />
              <span>
                Sobrenome inferido do email <code className="font-mono">{contato.email}</code>:
                {' '}<strong>{inferredLastName}</strong>
              </span>
            </div>
          )}

          {/* Progresso da cascata */}
          {strategyLog.length > 0 && step !== 'diff' && step !== 'candidates' && (
            <div className="mb-3 p-2 bg-slate-50 border border-slate-200 rounded space-y-0.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Cascata de busca
              </div>
              {strategyLog.map((s, i) => (
                <div key={i} className="text-[11px] flex items-center gap-1.5">
                  {s.status === 'running' && <Loader2 size={10} className="animate-spin text-blue-500" />}
                  {s.status === 'ok' && <Check size={10} className="text-emerald-600" />}
                  {s.status.startsWith('ok') && s.status !== 'ok' && <Check size={10} className="text-emerald-600" />}
                  {s.status === 'fail' && <X size={10} className="text-red-500" />}
                  {s.status === '0 resultados' && <X size={10} className="text-slate-400" />}
                  <span className={
                    s.status === 'running' ? 'text-blue-700 font-medium' :
                    s.status === 'fail' || s.status === '0 resultados' ? 'text-slate-400' :
                    'text-slate-700'
                  }>
                    {s.label}
                    {s.status !== 'running' && s.status !== 'ok' && s.status !== 'fail'
                      ? <span className="text-slate-500 ml-1">— {s.status}</span>
                      : null}
                  </span>
                </div>
              ))}
            </div>
          )}

          {step === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4 justify-center">
              <Loader2 size={16} className="animate-spin" /> Buscando no Apollo...
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 mb-3">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {step === 'done' && strategy === 'not_found' && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2 mb-3">
              <p className="text-xs text-blue-800 font-medium">
                Cole a URL do LinkedIn do contato (use os botões LinkedIn/Google para encontrar):
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualLinkedinUrl}
                  onChange={(e) => setManualLinkedinUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/nome-da-pessoa"
                  className="flex-1 px-2 py-1.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={retrying}
                />
                <button
                  onClick={handleManualLinkedinRetry}
                  disabled={retrying || !manualLinkedinUrl.trim().includes('linkedin.com/in/')}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {retrying ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Buscar
                </button>
              </div>
            </div>
          )}

          {step === 'candidates' && (
            <div>
              <div className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                <Search size={11} /> Apollo encontrou {candidates.length} candidatos. Escolha:
              </div>
              <div className="space-y-1">
                {candidates.map(c => (
                  <button
                    key={c.apollo_person_id}
                    onClick={() => handleEscolherCandidato(c)}
                    className="w-full text-left p-2 bg-white border border-slate-200 hover:border-amber-400 hover:bg-amber-50/30 rounded flex items-start gap-2 transition-colors"
                  >
                    <div className="flex-grow min-w-0">
                      <div className="text-xs font-medium text-slate-800 truncate">{c.name || '—'}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {c.title || '—'}{c.company ? ` @ ${c.company}` : ''}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'diff' && apolloData && (
            <>
              <div className="text-[11px] text-slate-500 mb-2">
                Marque os campos para atualizar. Já pré-selecionados os que divergem.
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="py-1 px-1 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-6"></th>
                    <th className="py-1 px-1 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Campo</th>
                    <th className="py-1 px-1 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CRM</th>
                    <th className="py-1 px-1 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Apollo</th>
                  </tr>
                </thead>
                <tbody>
                  {CAMPOS_DIFF.map(f => {
                    const cv = contato[f.key];
                    const av = getApolloVal(apolloData, f);
                    if (!av || String(av).trim() === '') return null;
                    const diverge = String(av).trim() !== String(cv || '').trim();
                    return (
                      <tr key={f.key} className={`border-b border-slate-100 ${diverge ? 'bg-amber-50/30' : ''}`}>
                        <td className="py-1 px-1">
                          <input
                            type="checkbox"
                            checked={selectedFields.has(f.key)}
                            onChange={() => handleToggleField(f.key)}
                            className="h-3.5 w-3.5"
                          />
                        </td>
                        <td className="py-1 px-1 text-slate-700 font-medium">{f.label}</td>
                        <td className="py-1 px-1 text-slate-500 truncate max-w-[130px]" title={cv || ''}>
                          {cv || <span className="italic text-slate-400">vazio</span>}
                        </td>
                        <td className={`py-1 px-1 truncate max-w-[160px] ${diverge ? 'text-amber-800 font-medium' : 'text-slate-600'}`} title={av}>
                          {av}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {apolloData.apollo_person_id && !contato.apollo_person_id && (
                <div className="mt-2 text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded p-1.5">
                  apollo_person_id (<code className="font-mono">{apolloData.apollo_person_id}</code>) será gravado, permitindo enrichments futuros diretos.
                </div>
              )}
              {apolloData.company_name && empresaLabel
                && apolloData.company_name.trim().toLowerCase() !== empresaLabel.trim().toLowerCase() && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded">
                  <div className="font-semibold text-amber-900 text-xs flex items-center gap-1 mb-1">
                    ⚠️ Mudança de empresa detectada
                  </div>
                  <div className="text-[11px] text-amber-800 mb-2">
                    Apollo indica que <strong>{nomeCompleto}</strong> está em
                    {' '}<strong>{apolloData.company_name}</strong>, mas no CRM está vinculado a
                    {' '}<strong>{empresaLabel}</strong>.
                  </div>
                  <div className="space-y-1 text-[11px] text-slate-700">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyEndEmployment}
                        onChange={(e) => setApplyEndEmployment(e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      Encerrar emprego em <strong>{empresaLabel}</strong> (is_current=false, end_date=hoje)
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyCreateEmployment}
                        onChange={(e) => setApplyCreateEmployment(e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      Criar emprego em <strong>{apolloData.company_name}</strong> (is_current=true)
                    </label>
                    <div className="text-[10px] text-amber-700 italic pl-5">
                      Nova empresa será criada automaticamente no CRM se não existir (MAIÚSCULO).
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 'applying' && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Aplicando mudanças...
            </div>
          )}

          {step === 'done' && !error && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900">
              <Check size={14} /> Mudanças aplicadas. Evidence trail registrado.
            </div>
          )}
        </div>

        <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          {step === 'diff' && (
            <>
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-200 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={handleApplyFields}
                disabled={selectedFields.size === 0}
                className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Check size={12} /> Aplicar ({selectedFields.size})
              </button>
            </>
          )}
          {(step === 'done' || step === 'candidates') && (
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApolloEnrichContactModal;
